<template>
  <main>
    <SectionHero class="-md:!min-h-[65svh] md:min-h-[700px] md:max-h-[850px] md:h-[83vh]">
      <OrganismHero class="-md:!min-h-[65svh]" :hero="hero" />
    </SectionHero>
    <SectionDefault>
      <div class="px-3 flex flex-col md:px-5">
        <div v-if="tags?.length != 0" class="hidden md:flex mb-4 flex-wrap gap-1 md:gap-2">
          <MoleculeSwitch
            v-for="tag in tags"
            :id="tag.attributes.slug"
            :key="tag.id"
            :label="tag.attributes.title"
            :name="tag.attributes.slug"
            :selected="selectedTags.includes(tag.attributes.slug)"
            @select="onSwitchSelect"
          />
        </div>
        <AtomButtonDropdown
          v-if="$device.isMobileOrTablet"
          class="md:hidden w-full mb-3"
          :auto-width="true"
          :theme-light="true"
          :items="tagsDropdown"
          :initial-index="
            selectedTags.length == 0 ? 0 : tags.findIndex(({ attributes: { slug } }) => selectedTags.includes(slug)) + 1
          "
          @select="onTagSelect"
        />

        <div
          class="grid grid-flow-row-dense md:pt-5 md:border-t border-t-gray-500 gap-3 md:gap-5 md:grid-cols-4 grid-cols-1"
        >
          <InsightCard
            v-for="insight in insightsGrid"
            :id="insight.slug"
            :key="insight.id"
            :category="insight.tags"
            :title="insight.title"
            :image="insight.imagePreview || insight.image"
            :duration="insight.time_to_read"
            :date="insight.date"
            :class="{ 'md:col-span-2': insight.card_form == 'rect' }"
          />
        </div>
        <AtomButton
          :disabled="loading"
          :class="{
            'pointer-events-none animate-pulse': loading,
            'invisible h-0 -md:!mb-0': total == insightsGrid.length,
          }"
          class="md:mt-11 md:mb-48 mt-5 mb-24 mx-auto"
          @click="loadMore"
          >БОЛЬШЕ СТАТЕЙ...</AtomButton
        >
      </div>
      <FormLearnMore />
    </SectionDefault>
  </main>
</template>

<script>
import MoleculeSwitch from '../../components/molecules/forms/MoleculeSwitch.vue'
import InsightCard from '../../components/molecules/InsightCard.vue'
import AtomButton from '../../components/atoms/AtomButton.vue'
import OrganismHero from '../../components/organisms/layouts/OrganismHero'
import SectionHero from '../../components/templates/layouts/SectionHero'
import SectionDefault from '../../components/templates/layouts/SectionDefault'
import FormLearnMore from '../../components/organisms/builder/FormLearnMore.vue'
import AtomButtonDropdown from '~/components/atoms/AtomButtonDropdown'

export default {
  name: 'Insights',
  components: {
    SectionHero,
    OrganismHero,
    InsightCard,
    SectionDefault,
    FormLearnMore,
    MoleculeSwitch,
    AtomButton,
    AtomButtonDropdown,
  },
  meta: {
    theme: 'dark',
  },
  async asyncData({ $axios, route, $device }) {
    const [
      {
        data: {
          data: insights,
          meta: { total },
        },
      },
      {
        data: { data: tags },
      },
    ] = await Promise.all([
      $axios.get('/api/article', { params: route.query }).catch(() => []),
      $axios.get('/api/tags').catch(() => []),
    ])

    const cellPerPageMobile = 4
    const cellPerPageDesktop = 12
    const displayAmountStep = $device.isMobileOrTablet ? cellPerPageMobile : cellPerPageDesktop

    return { insights, tags, page: 1, total, displayAmount: displayAmountStep, displayAmountStep }
  },
  data: c => {
    return {
      loading: false,
      total: 0,
      displayAmount: 0,
      displayAmountStep: 0,
      page: 1,
      insights: [],
      showModal: false,
      modalSelectedTags: new Set([]),
      tags: [],
      hero: {
        title1: 'encore',
        title2: 'insider',
        desc: 'Мы создали Encore Insider для того, чтобы делиться с вами многолетней экспертизой от лучших специалистов в различных сферах нашей деятельности, рассказывать об интересных исследованиях и трендах в индустрии, а также – освещать значимые новости наших клубов.',
        breadcrumbs: c.$breadcrumbs.insights(),
      },
    }
  },
  computed: {
    insightsGrid() {
      const last = this.$device.isMobileOrTablet
        ? this.displayAmount + this.displayAmountStep
        : this.insights.reduce(
            (acc, item, i) =>
              acc.sum + (item.card_form === 'rect' ? 2 : 1) >= this.displayAmountStep * this.page && acc.index == null
                ? { ...acc, index: i }
                : { ...acc, sum: acc.sum + (item.card_form === 'rect' ? 2 : 1) },
            { sum: 0, index: null },
          ).index || this.insights.length - 1

      return this.insights.slice(0, last + 1)
    },
    selectedTags() {
      return [this.$route.query.tag].flat().filter(Boolean)
    },
    tagsDropdown() {
      return [{ text: 'Все' }, ...this.tags.map(({ attributes: { title } }) => ({ text: title }))]
    },
  },
  watchQuery: ['tag'],
  methods: {
    async loadMore() {
      if (this.insights.length - this.displayAmount >= this.displayAmountStep) {
        this.displayAmount += this.displayAmountStep
        return
      }
      this.loading = true
      this.page += 1

      const {
        data: { data: newInsights },
      } = await this.$axios.get('/api/article', {
        params: { ...this.$route.query, page: this.page },
      })

      this.insights = [...this.insights, ...newInsights]
      this.loading = false
    },
    onSwitchSelect({ value, id }) {
      this.updateRouteQuery(value, id)
    },
    updateRouteQuery(value, key) {
      const tags = new Set([this.$route.query.tag].flat().filter(Boolean))

      value ? tags.add(key) : tags.delete(key)

      this.$router.replace({
        query: {
          ...this.$route.query,
          tag: [...tags],
        },
      })
    },
    onTagSelect(i) {
      if (i == 0) {
        this.clearFilters()
      } else {
        this.$router.replace({
          query: {
            ...this.$route.query,
            tag: this.tags[i - 1].attributes.slug,
          },
        })
      }
    },
    clearFilters() {
      this.$router.replace({
        query: {
          ...this.$route.query,
          tag: [],
        },
      })
    },
  },
}
</script>
